import { useEffect, useId, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Download, FileSearch, FileText, FolderOpen } from 'lucide-react';
import { portalQueries } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { canAccess, formatDate, formatFileSize } from '@blimo/shared';
import { Button, DocumentViewer, EmptyState, ErrorState, Label, SearchInput, Select, Skeleton, cn } from '@blimo/ui';
import { PageHeader } from '../components/ui/page-header.jsx';
import { ResponsiveSheet } from '../components/ui/responsive-sheet.jsx';
import { ItemList, ItemRow, Section } from '../components/ui/section.jsx';
import { unitTitle } from '../lib/units.js';
import { ForbiddenPage } from './pages.jsx';

/** @param {any} doc */
const docName = (doc) => doc.title || doc.original_name || `Dokument #${doc.id}`;

/** Ab dieser Länge sucht die Seite serverseitig im Volltext (Titel, Dateiname, Inhalt). */
export const DOCUMENT_SEARCH_MIN_LENGTH = 2;

/**
 * Dokumente (F-405, F-807; `documents.view`): veröffentlichte Dokumente der eigenen Liegenschaften/Einheiten und
 * allgemeine Dokumente, gruppiert nach Bezug → Ordner. Filter Liegenschaft / Allgemein (API).
 * Suche (ab 2 Zeichen): Volltextsuche `GET /portal/documents/search` – Titel, Dateiname und Inhalt, mit Textausschnitt;
 * Sichtbarkeit prüft der Server (auch freigegebene Belegprüfungs-Ordner). `?document=` hebt ein Dokument hervor (Blimo AI).
 */
export function DocumentsPage() {
  const { data: me } = useMe();
  if (!canAccess(me, { permissions: ['documents.view'] })) return <ForbiddenPage />;
  return <DocumentsView />;
}

function DocumentsView() {
  const api = useApi();
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const filterId = useId();
  const [q, setQ] = useState('');
  const [viewing, setViewing] = useState(/** @type {any} */ (null));

  const params = search.scope === 'global' ? { scope: 'global' } : search.property ? { property: search.property } : {};
  const query = useQuery(portalQueries.documents(api, params));
  const properties = useQuery(portalQueries.properties(api));
  const term = q.trim();
  const searching = term.length >= DOCUMENT_SEARCH_MIN_LENGTH;
  const results = useQuery(portalQueries.documentSearch(api, searching ? term : ''));

  /** owner key → label ("Allgemein", property name, "Lindenhof · WE 01") */
  const ownerLabel = useMemo(() => {
    const props = properties.data ?? [];
    /** @param {any} folder */
    return (folder) => {
      if (folder.owner_label) return folder.owner_label;
      if (!folder.owner_type) return 'Allgemeine Dokumente';
      if (folder.owner_type === 'property') {
        const p = props.find((x) => String(x.id) === String(folder.owner_id));
        return p?.name || `Liegenschaft #${folder.owner_id}`;
      }
      if (folder.owner_type === 'unit') {
        for (const p of props) {
          const u = (p.my_units ?? []).find((x) => String(x.id) === String(folder.owner_id));
          if (u) return [p.name, unitTitle(u)].filter(Boolean).join(' · ');
        }
        return `Einheit #${folder.owner_id}`;
      }
      return 'Weitere Dokumente';
    };
  }, [properties.data]);

  const groups = useMemo(() => {
    /** @type {Map<string, { key: string, label: string, folders: any[] }>} */
    const map = new Map();
    for (const folder of query.data ?? []) {
      const documents = folder.documents ?? [];
      if (documents.length === 0) continue;
      const key = `${folder.owner_type ?? 'global'}:${folder.owner_id ?? ''}`;
      if (!map.has(key)) map.set(key, { key, label: ownerLabel(folder), folders: [] });
      map.get(key).folders.push({ ...folder, documents });
    }
    return [...map.values()];
  }, [query.data, ownerLabel]);

  const highlighted = search.document;
  const highlightedVisible = highlighted != null && groups.some((g) => g.folders.some((f) => f.documents.some((d) => d.id === highlighted)));

  useEffect(() => {
    if (!highlightedVisible) return;
    const el = document.getElementById(`document-${highlighted}`);
    el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [highlighted, highlightedVisible]);

  /** @param {Record<string, any>} patch */
  const setSearch = (patch) =>
    navigate({
      to: '/dokumente',
      search: (prev) => Object.fromEntries(Object.entries({ ...prev, ...patch }).filter(([, v]) => v !== undefined && v !== '')),
      replace: true,
    });

  const filterValue = search.scope === 'global' ? 'global' : search.property ? `p:${search.property}` : '';

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Dokumente" description="Unterlagen zu Ihren Liegenschaften und Einheiten, bereitgestellt von Ihrer Verwaltung." />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex flex-col gap-1">
          <Label htmlFor={filterId} className="text-xs! text-muted-foreground">
            Bereich
          </Label>
          <Select
            id={filterId}
            value={filterValue}
            wrapperClassName="sm:w-64"
            onChange={(e) => {
              const v = e.target.value;
              setSearch({ scope: v === 'global' ? 'global' : undefined, property: v.startsWith('p:') ? Number(v.slice(2)) : undefined, document: undefined });
            }}
          >
            <option value="">Alle Dokumente</option>
            <option value="global">Allgemeine Dokumente</option>
            {(properties.data ?? []).map((p) => (
              <option key={p.id} value={`p:${p.id}`}>
                {p.name || `Liegenschaft #${p.id}`}
              </option>
            ))}
          </Select>
        </div>
        <SearchInput
          value={q}
          onValueChange={setQ}
          delay={300}
          label="Dokumente durchsuchen"
          placeholder="Titel und Inhalt durchsuchen"
          wrapperClassName="sm:w-80"
        />
      </div>

      {highlighted != null && query.isSuccess && !highlightedVisible && (
        <p role="status" className="rounded-xl border bg-card px-4 py-3 text-[0.8125rem] text-muted-foreground">
          Das verlinkte Dokument ist in der aktuellen Auswahl nicht enthalten.{' '}
          <button
            type="button"
            className="font-medium text-(--primary-text) hover:underline"
            onClick={() => (setQ(''), setSearch({ scope: undefined, property: undefined }))}
          >
            Alle Dokumente anzeigen
          </button>
        </p>
      )}

      {searching ? (
        <SearchResults results={results} term={term} onOpen={setViewing} />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : query.isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : groups.length === 0 ? (
        <EmptyState icon={FolderOpen} title="Keine Dokumente" description="Für diese Auswahl hat Ihre Verwaltung noch keine Dokumente veröffentlicht." />
      ) : (
        <div className={cn('flex flex-col gap-8', query.isPlaceholderData && 'opacity-60')}>
          {groups.map((group) => (
            <section key={group.key} aria-label={group.label} className="flex flex-col gap-3">
              <h2 className="text-base font-semibold">{group.label}</h2>
              <div className="grid items-start gap-4 xl:grid-cols-2">
                {group.folders.map((folder) => (
                  <Section
                    key={folder.id}
                    title={folder.name}
                    description={`${folder.documents.length} ${folder.documents.length === 1 ? 'Dokument' : 'Dokumente'}`}
                    headingLevel="h3"
                    flush
                  >
                    <ItemList aria-label={`${group.label} – ${folder.name}`}>
                      {folder.documents.map((doc) => {
                        const active = doc.id === highlighted;
                        const meta = [
                          doc.published_at || doc.created_at ? formatDate(doc.published_at || doc.created_at) : null,
                          doc.size != null ? formatFileSize(doc.size) : null,
                        ]
                          .filter(Boolean)
                          .join(' · ');
                        return (
                          <li
                            key={doc.id}
                            id={`document-${doc.id}`}
                            data-highlight={active || undefined}
                            className="flex items-center pr-2 data-[highlight]:bg-(--primary-tint)"
                          >
                            <ItemRow
                              as="button"
                              icon={FileText}
                              chevron={false}
                              aria-current={active ? 'true' : undefined}
                              onClick={() => {
                                setViewing(doc);
                                if (!active) setSearch({ document: doc.id });
                              }}
                              title={docName(doc)}
                              titleClassName={active ? 'font-semibold' : undefined}
                              subtitle={[meta, doc.description].filter(Boolean).join(' · ') || undefined}
                              className="min-w-0 flex-1 hover:bg-transparent"
                            />
                            <Button asChild variant="ghost" size="icon-sm" className="shrink-0 text-muted-foreground hover:text-foreground">
                              <a href={api.portal.documents.downloadUrl(doc.id)} download aria-label={`${docName(doc)} herunterladen`} title="Herunterladen">
                                <Download aria-hidden="true" />
                              </a>
                            </Button>
                          </li>
                        );
                      })}
                    </ItemList>
                  </Section>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <ResponsiveSheet open={viewing != null} onOpenChange={(open) => !open && setViewing(null)} title={viewing ? docName(viewing) : 'Dokument'} size="lg">
        {viewing && (
          <DocumentViewer
            title={docName(viewing)}
            mimeType={viewing.mime_type}
            src={api.portal.documents.downloadUrl(viewing.id, { inline: true })}
            downloadHref={api.portal.documents.downloadUrl(viewing.id)}
          />
        )}
      </ResponsiveSheet>
    </div>
  );
}

/**
 * Treffer der Volltextsuche: Titel, Fundorte (Ordner/Objekt bzw. Belegprüfung), Textausschnitt mit hervorgehobenen
 * Treffern (Segmente vom Server, reiner Text). Belegprüfungs-Dokumente öffnen die Belegprüfung (eigene Download-Regeln).
 * @param {{ results: any, term: string, onOpen: (doc: any) => void }} props
 */
export function SearchResults({ results, term, onOpen }) {
  const api = useApi();
  if (results.error) return <ErrorState error={results.error} onRetry={() => results.refetch()} />;
  if (results.isPending || (results.isPlaceholderData && results.isFetching && !results.data?.length)) return <Skeleton className="h-48 w-full" />;
  const hits = results.data ?? [];
  if (hits.length === 0) {
    return (
      <EmptyState
        icon={FileSearch}
        title="Keine Treffer"
        description={`Kein Dokument enthält „${term}“ in Titel, Dateiname oder Text. Gescannte Dokumente ohne Textebene werden nur über den Titel gefunden.`}
      />
    );
  }
  return (
    <Section
      title="Suchergebnisse"
      description={`${hits.length} ${hits.length === 1 ? 'Dokument' : 'Dokumente'} für „${term}“`}
      headingLevel="h2"
      flush
      className={cn(results.isPlaceholderData && 'opacity-60')}
    >
      <ItemList aria-label="Suchergebnisse">
        {hits.map((hit) => {
          const check = hit.locations?.find((l) => l.kind === 'document_check');
          const folders = (hit.locations ?? []).filter((l) => l.kind === 'folder');
          const where = [...folders.map((l) => `${l.label} › ${l.folder}`), ...(check ? [`${check.label} › ${check.folder}`] : [])].join(' · ');
          return (
            <li key={hit.id} className="flex flex-col gap-1 px-4 py-3">
              <div className="flex items-start gap-2">
                <FileText aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  {folders.length > 0 ? (
                    <button type="button" className="text-left text-sm font-medium hover:underline" onClick={() => onOpen(hit)}>
                      {docName(hit)}
                    </button>
                  ) : (
                    <Link to="/belegpruefung/$checkId" params={{ checkId: String(check?.check_id) }} className="text-sm font-medium hover:underline">
                      {docName(hit)}
                    </Link>
                  )}
                  <span className="text-xs text-muted-foreground">{[where, hit.page ? `Seite ${hit.page}` : null].filter(Boolean).join(' · ')}</span>
                  {hit.snippet?.length > 0 && (
                    <p className="text-[0.8125rem] leading-relaxed text-muted-foreground" data-testid="document-snippet">
                      {hit.snippet.map((s, i) =>
                        s.hit ? (
                          <mark key={i} className="rounded-sm bg-(--primary-tint-strong) px-0.5 text-foreground">
                            {s.text}
                          </mark>
                        ) : (
                          <span key={i}>{s.text}</span>
                        ),
                      )}
                    </p>
                  )}
                </div>
                {folders.length > 0 && (
                  <Button asChild variant="ghost" size="icon-sm" className="shrink-0 text-muted-foreground hover:text-foreground">
                    <a href={api.portal.documents.downloadUrl(hit.id)} download aria-label={`${docName(hit)} herunterladen`} title="Herunterladen">
                      <Download aria-hidden="true" />
                    </a>
                  </Button>
                )}
              </div>
            </li>
          );
        })}
      </ItemList>
    </Section>
  );
}
